import { apiClient } from '../apiClient';
import { API_ENDPOINTS } from './config';
import {
  BootstrapRequest,
  BootstrapResponse,
  BootstrapErrorResponse,
  BootstrapError,
} from '../../types/bootstrap';

/**
 * Service for managing bootstrap operations for company subscriptions
 * @feature PLATFORM_API_ACCESS - Provides API access for bootstrap management
 */
export class BootstrapService {
  /**
   * Bootstrap a company subscription with resumable capability
   * 
   * @param companyId - UUID of the company to bootstrap
   * @param options - Bootstrap options
   * @returns Promise resolving to BootstrapResponse
   * @throws BootstrapError when bootstrap fails (400 or 422 response with blocker info)
   */
  static async bootstrapCompanySubscription(
    companyId: string,
    options: {
      subscriptionTypeId?: string;
      userId?: string;
      forceRebuild?: boolean;
      cleanupPartial?: boolean;
      resumeFromFailure?: boolean;
    } = {}
  ): Promise<BootstrapResponse> {
    if (!companyId) {
      throw new Error('Company ID is required for bootstrap');
    }

    const params = new URLSearchParams();
    
    if (options.subscriptionTypeId) {
      params.append('subscription_type_id', options.subscriptionTypeId);
    }
    if (options.userId) {
      params.append('user_id', options.userId);
    }
    if (options.forceRebuild !== undefined) {
      params.append('force_rebuild', String(options.forceRebuild));
    }
    if (options.cleanupPartial !== undefined) {
      params.append('cleanup_partial', String(options.cleanupPartial));
    }
    if (options.resumeFromFailure !== undefined) {
      params.append('resume_from_failure', String(options.resumeFromFailure));
    }

    const endpoint = API_ENDPOINTS.SUBSCRIPTIONS.BOOTSTRAP_COMPANY(companyId);
    const url = `${endpoint}${params.toString() ? '?' + params.toString() : ''}`;

    try {
      const response = await apiClient.post<BootstrapResponse>(url);
      return response.data;
    } catch (error: any) {
      // Log full error response for debugging
      console.error('Bootstrap error:', error);
      console.error('Full error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error detail:', error.response?.data?.detail);
      
      // Handle 422 (Unprocessable Entity) - blocker response with detailed information
      if (error.response?.status === 422 && error.response?.data?.detail) {
        // This is a blocker response - extract the detail
        const errorDetail = error.response.data.detail as BootstrapErrorResponse['detail'];
        console.error('Bootstrap blocker details:', {
          message: errorDetail.message,
          blocker: errorDetail.blocker,
          progress: errorDetail.progress,
          completed_steps: errorDetail.completed_steps,
          failed_steps: errorDetail.failed_steps,
          resume_info: errorDetail.resume_info
        });
        throw new BootstrapError(errorDetail);
      }
      
      // Handle 400 (Bad Request) - legacy blocker response
      if (error.response?.status === 400 && error.response?.data?.detail) {
        const errorDetail = error.response.data.detail as BootstrapErrorResponse['detail'];
        throw new BootstrapError(errorDetail);
      }
      
      throw error;
    }
  }
}

